/**
 * @file     storageCache.ts
 * @purpose  Versioned, TTL-aware storage envelope helpers (read, write, clear, fallback) for safely persisting typed values to sessionStorage or localStorage.
 */
import type { StorageDriver } from '@/core/utils/browserRuntime';

type StorageEnvelope<TValue> = {
  version: number;
  updatedAt: number;
  value: TValue;
};

type ReadStorageEnvelopeOptions<TValue> = {
  key: string;
  storage: StorageDriver | null;
  version: number;
  ttlMs?: number;
  validate: (value: unknown) => value is TValue;
};

type ReadStorageEnvelopeWithFallbackOptions<TValue> =
  ReadStorageEnvelopeOptions<TValue> & {
    fallbackStorage?: StorageDriver | null;
  };

type WriteStorageEnvelopeOptions<TValue> = {
  key: string;
  storage: StorageDriver | null;
  value: TValue;
  version: number;
};

const isStorageEnvelope = (value: unknown): value is StorageEnvelope<unknown> => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<StorageEnvelope<unknown>>;
  return (
    typeof candidate.version === 'number' &&
    Number.isFinite(candidate.version) &&
    typeof candidate.updatedAt === 'number' &&
    Number.isFinite(candidate.updatedAt) &&
    'value' in candidate
  );
};

export const clearStorageEnvelope = (
  storage: StorageDriver | null,
  key: string
) => {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
};

export const readStorageEnvelope = <TValue>({
  key,
  storage,
  version,
  ttlMs,
  validate
}: ReadStorageEnvelopeOptions<TValue>): TValue | null => {
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(key);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!isStorageEnvelope(parsed) || parsed.version !== version) {
      clearStorageEnvelope(storage, key);
      return null;
    }

    if (typeof ttlMs === 'number' && Date.now() - parsed.updatedAt > ttlMs) {
      clearStorageEnvelope(storage, key);
      return null;
    }

    if (!validate(parsed.value)) {
      clearStorageEnvelope(storage, key);
      return null;
    }

    return parsed.value;
  } catch {
    clearStorageEnvelope(storage, key);
    return null;
  }
};

export const readStorageEnvelopeWithFallback = <TValue>({
  key,
  storage,
  fallbackStorage,
  version,
  ttlMs,
  validate
}: ReadStorageEnvelopeWithFallbackOptions<TValue>): TValue | null => {
  const primaryValue = readStorageEnvelope({
    key,
    storage,
    version,
    ttlMs,
    validate
  });

  if (primaryValue !== null) {
    return primaryValue;
  }

  if (!fallbackStorage || fallbackStorage === storage) {
    return null;
  }

  const fallbackValue = readStorageEnvelope({
    key,
    storage: fallbackStorage,
    version,
    ttlMs,
    validate
  });

  if (fallbackValue === null) {
    return null;
  }

  if (storage) {
    writeStorageEnvelope({
      key,
      storage,
      value: fallbackValue,
      version
    });
  }

  clearStorageEnvelope(fallbackStorage, key);
  return fallbackValue;
};

export const writeStorageEnvelope = <TValue>({
  key,
  storage,
  value,
  version
}: WriteStorageEnvelopeOptions<TValue>) => {
  if (!storage) {
    return;
  }

  try {
    const envelope: StorageEnvelope<TValue> = {
      version,
      updatedAt: Date.now(),
      value
    };

    storage.setItem(key, JSON.stringify(envelope));
  } catch {
    // Ignore storage failures.
  }
};
